import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { type Plugin } from '../../context/PluginContext';
import apiClient from '../../api/client';
import { TableRowSkeleton } from '../../components/admin/TableRowSkeleton';
import { useNotificationDispatch } from '../../context/NotificationContext';
import { APP_CONFIG } from '../../config/constants';

// Hook para Debounce
function useDebounce(value: string, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

export default function AdminPluginsPage() {
    const [plugins, setPlugins] = useState<Plugin[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Estados para busca e paginação
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const { showNotification } = useNotificationDispatch();
    const limit = 20;
    const totalPages = Math.ceil(totalCount / limit);

    const fetchPlugins = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/admin/plugins', {
                params: { 
                    search: debouncedSearchTerm,
                    page: currentPage,
                    limit: limit
                }
            });
            setPlugins(response.data.plugins);
            setTotalCount(response.data.totalCount);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Falha ao buscar os plugins.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchTerm, currentPage]);

    useEffect(() => {
        fetchPlugins();
    }, [fetchPlugins]);

    // Reseta a página para 1 quando a busca mudar
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    const handleDelete = async (pluginId: string, pluginName: string) => {
        if (!window.confirm(`Tem certeza que deseja deletar o plugin "${pluginName}"? Esta ação é irreversível.`)) {
            return;
        }
        try {
            await apiClient.delete(`/admin/plugins/${pluginId}`);
            showNotification(`Plugin "${pluginName}" deletado com sucesso!`, 'success');
            fetchPlugins(); // Atualiza a lista
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Falha ao deletar o plugin.';
            showNotification(errorMessage, 'error');
        }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h1 className="text-3xl font-bold text-text-primary mb-4 sm:mb-0">Gerenciar Plugins ({totalCount})</h1>
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                    <input
                        type="text"
                        placeholder="Buscar por nome ou ID..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="form-input w-full sm:w-72"
                    />
                    <Link to="/admin/plugins/new" className="w-full sm:w-auto text-center bg-brand-blue text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap">
                        Adicionar Novo Plugin
                    </Link>
                </div>
            </div>

            {error && <p className="text-red-500 bg-red-100 p-4 rounded-md mb-4">{error}</p>}

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <table className="min-w-full hidden md:table">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="th-cell text-left">Plugin</th>
                            <th className="th-cell">Preço</th>
                            <th className="th-cell">Status</th>
                            <th className="th-cell text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {loading ? (
                            Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} columns={4} />)
                        ) : (
                            plugins.map(plugin => (
                                <tr key={plugin.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="td-cell">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-blue-100 to-purple-100 flex-shrink-0 border-2 border-white shadow-sm">
                                                {plugin.iconUrl ? (
                                                    <img 
                                                        src={`${APP_CONFIG.uploadsBaseUrl}${plugin.iconUrl}`} 
                                                        alt={plugin.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                            <span className="font-medium text-gray-900">{plugin.name}</span>
                                        </div>
                                    </td>
                                    <td className="td-cell">
                                        <span className="font-semibold text-gray-700">
                                            {plugin.price === 0 ? 'Grátis' : `R$ ${plugin.price.toFixed(2)}`}
                                        </span>
                                    </td>
                                    <td className="td-cell">
                                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${
                                            plugin.status === 'published' 
                                                ? 'bg-green-50 text-green-700 border-green-200' 
                                                : plugin.status === 'draft'
                                                ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                                : 'bg-gray-50 text-gray-700 border-gray-200'
                                        }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                                plugin.status === 'published' ? 'bg-green-500' : 
                                                plugin.status === 'draft' ? 'bg-yellow-500' : 'bg-gray-500'
                                            }`}></span>
                                            {plugin.status === 'published' ? 'Publicado' : plugin.status === 'draft' ? 'Rascunho' : 'Arquivado'}
                                        </span>
                                    </td>
                                    <td className="td-cell text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <Link 
                                                to={`/admin/plugins/edit/${plugin.id}`} 
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 border border-indigo-200 transition-all"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                Editar
                                            </Link>
                                            <button 
                                                onClick={() => handleDelete(plugin.id, plugin.name)} 
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 border border-red-200 transition-all"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                Eliminar
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                {/* Mobile layout remains largely the same, but you might want to adapt it to use the paginated data */}
            </div>

            {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                        Anterior
                    </button>
                    <span className="text-sm text-gray-700">Página {currentPage} de {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                        Próxima
                    </button>
                </div>
            )}
        </div>
    );
}